import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  debounce,
  styled,
} from '@mui/material';
import Text from 'components/text';
import { useCallback, useMemo, useState } from 'react';
import api from 'utils/api';

import { useError } from 'hooks/useError';

import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { emailRule } from 'utils/validation-rules';
import * as yup from 'yup';

import BaseInput from 'components/inputs/base-input';
import PurpleButton from 'components/purple-button';
import AutocompleteInputController from 'controllers/autocomplete-input-controller';
import BaseInputController from 'controllers/base-input-controller';
import CheckboxController from 'controllers/checkbox-controller';
import copyToClipboard from 'utils/copyToClipboard';
import { isYoutubeChannelId, youtubeChannelName, youtubeToId } from '../../../../utils/youtube';
import { useTranslation } from 'react-i18next';
import { supportedLanguages } from '../../../../i18n/config';
import { colors } from 'styles/colors';
import { IArtistOption, spotifyArtistsToOptions } from 'utils/helpers';
import { ISpotifyData } from 'utils/apiTypes';

/** One row of `GET /admin/customer-file/search` (SSM D-61). */
export interface CustomerFileHit {
  hubspotDealId: string | null;
  airtableRecordId: string;
  hubspotCompanyId: string | null;
  kind: 'artist' | 'label';
  name: string | null;
  email: string | null;
  spotifyId: string | null;
  youtubeId: string | null;
  totalAdvance: number | null;
  contractLengthYears: number | null;
  distributionStatus: 'redistributed' | 'other';
  analyticsAccess: boolean;
  language: string | null;
  alreadyInvited: boolean;
  dealId: number | null;
  /** What the row still lacks to be invited (SSM D-77); empty when eligible. */
  blockers: string[];
}

type Lookup = 'ok' | 'bad' | 'noid' | 'blocked' | 'warn' | null;

/** Row-level blockers the admin cannot fix in the form; the rest are editable fields. */
const ROW_BLOCKERS: Record<string, string> = {
  not_closed_won: 'not Closed Won',
  missing_contract: 'no contract file',
};

const DISTRIBUTION_LABELS: Record<string, string> = {
  redistributed: 'Re-distributed to Snafu',
  other: 'Other',
};

const isValidYoutube = (value: unknown) => {
  if (value == null) return true; // `required` reports the empty case
  if (typeof value !== 'string') return false;
  const id = youtubeToId(value);
  return (id !== null && isYoutubeChannelId(id)) || youtubeChannelName(value) !== null;
};

const isNewSignedDeal = (artistStatus: string, dealId: number | null) =>
  artistStatus === 'signed' && !dealId;

/** Deal figure, prefilled from the customer file and editable; required for a new signed deal. */
const dealFigure = yup
  .number()
  .transform((value, original) => (original === '' || original == null ? null : value))
  .nullable()
  .typeError('Must be a number')
  .min(0, 'Must be 0 or more')
  .when(['artistStatus', 'dealId'], {
    is: isNewSignedDeal,
    then: (schema) => schema.required('Field is required'),
  });

/**
 * New Invitation form (SSM D-61). Signed: the picked customer-file row prefills
 * every field and all of them stay editable; the figures are required. Already
 * invited: `dealId` is set, only a different email is needed. Unsigned: no deal,
 * no figures. A label needs no identity; an artist needs a Spotify or a YouTube id.
 * Every validated field is rendered, so each error is visible under its field.
 */
export const inviteSchema = yup.object({
  artistStatus: yup.string().oneOf(['signed', 'unsigned']).required('Field is required'),
  dealId: yup.number().nullable(),
  rowEmail: yup.string().nullable(),
  // Set by the picked customer-file row; Send stays disabled until a row is found.
  hubspotDealId: yup.string().nullable().trim(),
  companyKind: yup.string().oneOf(['artist', 'label']).required('Field is required'),
  identityMode: yup.string().oneOf(['spotify', 'youtube']).required(),
  spotifyId: yup
    .string()
    .nullable()
    .when(['companyKind', 'identityMode', 'dealId'], {
      is: (kind: string, mode: string, dealId: number | null) =>
        kind === 'artist' && mode === 'spotify' && !dealId,
      then: yup.string().nullable().required('Field is required'),
    }),
  youtubeId: yup
    .string()
    .nullable()
    .when(['companyKind', 'identityMode', 'dealId'], {
      is: (kind: string, mode: string, dealId: number | null) =>
        kind === 'artist' && mode === 'youtube' && !dealId,
      then: yup
        .string()
        .nullable()
        .required('Field is required')
        .test('youtubeId', 'Invalid Youtube ID', isValidYoutube),
    }),
  artistName: yup.string().nullable().required('Field is required'),
  email: emailRule.test('differs', 'Enter a different email address', function (value) {
    const { dealId, rowEmail } = this.parent;
    if (!dealId || !value) return true;
    return value.trim().toLowerCase() !== (rowEmail || '').toLowerCase();
  }),
  advanceAmount: dealFigure,
  termLength: dealFigure,
  distributionStatus: yup
    .string()
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .oneOf(['redistributed', 'other', null])
    .when(['artistStatus', 'dealId'], {
      is: isNewSignedDeal,
      then: (schema) => schema.required('Field is required'),
    }),
  hasAnalyticsAccess: yup.boolean(),
  hasEducationAccess: yup.boolean(),
  language: yup.string().required('Field is required'),
});

const StyledButton = styled(PurpleButton)(() => ({
  margin: '8px 0px',
}));

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box display="flex" flexDirection="column" gap={0.5}>
    <Text fontSize={13} color="#555">
      {label}
    </Text>
    {children}
  </Box>
);

const hitLabel = (hit: CustomerFileHit) =>
  `${hit.name ?? 'Unnamed'} · ${hit.kind === 'label' ? 'Label' : 'Artist'} · Deal ${
    hit.hubspotDealId ?? '—'
  }`;

export default function NewInviteForm() {
  const { i18n } = useTranslation();
  const { message, setError } = useError();
  const [magicLink, setMagicLink] = useState('');
  const [lookup, setLookup] = useState<Lookup>(null);
  const [row, setRow] = useState<CustomerFileHit | null>(null);
  const [hits, setHits] = useState<CustomerFileHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [artistList, setArtistList] = useState<IArtistOption[]>([]);
  const [sending, setSending] = useState(false);

  const defaults = useMemo(
    () => ({
      artistStatus: 'signed',
      dealId: null as number | null,
      rowEmail: '',
      hubspotDealId: '',
      companyKind: 'artist',
      identityMode: 'spotify',
      spotifyId: null as string | null,
      youtubeId: null as string | null,
      artistName: '',
      email: '',
      advanceAmount: null as number | null,
      termLength: null as number | null,
      distributionStatus: 'other',
      hasAnalyticsAccess: false,
      hasEducationAccess: true,
      language: i18n.language in supportedLanguages ? i18n.language : 'en',
    }),
    [i18n.language]
  );

  const form = useForm({ defaultValues: defaults, resolver: yupResolver(inviteSchema) });
  const { watch, setValue, reset } = form;
  const artistStatus = watch('artistStatus');
  const companyKind = watch('companyKind');
  const identityMode = watch('identityMode');
  const dealId = watch('dealId');

  const spotifyId = watch('spotifyId');
  // The autocomplete only shows a value it is handed, so mirror the form value
  // (a searched artist, or the id prefilled from the customer-file row).
  const spotifyOption = useMemo(
    () =>
      spotifyId
        ? artistList.find((a) => a.value === spotifyId) ?? { label: spotifyId, value: spotifyId }
        : null,
    [spotifyId, artistList]
  );

  const signed = artistStatus === 'signed';
  const alreadyInvited = !!dealId;
  const found = lookup === 'ok' || lookup === 'bad';
  const open = found || !signed;
  const artist = open && companyKind === 'artist';
  const set = (name: any, value: any) => setValue(name, value, { shouldDirty: true });

  /** Back to the empty form of the given status. */
  const clearRow = (status: string = form.getValues('artistStatus')) => {
    setRow(null);
    setLookup(null);
    setHits([]);
    reset({ ...defaults, artistStatus: status }, { keepDefaultValues: true });
  };

  /**
   * Spotify is authoritative for the name (the server applies the same rule on
   * POST): overwrite the row name once the lookup answers, unless the admin has
   * meanwhile switched the id. On any failure the row name simply stays.
   */
  const prefillSpotifyName = async (id: string) => {
    try {
      const spArtist = (await api.spotify.getArtistById({ id })) as { name?: string } | undefined;
      if (spArtist?.name && form.getValues('spotifyId') === id) set('artistName', spArtist.name);
    } catch {
      // fallback: the customer-file name already in the field
    }
  };

  const applyRow = (hit: CustomerFileHit) => {
    setRow(hit);
    // No HubSpot deal id, no deal (SSM D-53): stop here, nothing to prefill.
    if (!hit.hubspotDealId) {
      setLookup('noid');
      return;
    }
    // Not eligible for a deal (SSM D-77): stop here as well.
    if (!hit.alreadyInvited && hit.blockers.some((b) => b in ROW_BLOCKERS)) {
      setLookup('blocked');
      return;
    }
    setLookup(hit.alreadyInvited ? 'bad' : 'ok');
    set('hubspotDealId', hit.hubspotDealId ?? '');
    set('dealId', hit.alreadyInvited ? hit.dealId : null);
    set('rowEmail', hit.email ?? '');
    set('companyKind', hit.kind);
    set('identityMode', hit.spotifyId || !hit.youtubeId ? 'spotify' : 'youtube');
    set('spotifyId', hit.spotifyId);
    set('youtubeId', hit.youtubeId);
    set('artistName', hit.name ?? '');
    if (hit.spotifyId) prefillSpotifyName(hit.spotifyId);
    set('email', hit.alreadyInvited ? '' : hit.email ?? '');
    set('advanceAmount', hit.totalAdvance);
    set('termLength', hit.contractLengthYears);
    set('distributionStatus', hit.distributionStatus);
    set('hasAnalyticsAccess', hit.analyticsAccess);
    set('hasEducationAccess', true);
    if (hit.language && hit.language in supportedLanguages) set('language', hit.language);
    form.clearErrors();
  };

  const search = useCallback(
    async (q: string) => {
      if (q.trim().length < 2) {
        setHits([]);
        return;
      }
      setSearching(true);
      try {
        const result = (await api.customerFile.search({ params: { q } })) as CustomerFileHit[];
        setHits(result);
        setLookup(result.length ? null : 'warn');
      } catch (err) {
        setError(err);
      } finally {
        setSearching(false);
      }
    },
    [setError]
  );
  const debouncedSearch = useMemo(() => debounce(search, 300), [search]);

  const searchArtist = useCallback(
    async (value: string) => {
      if (value === '') return;
      try {
        const data: ISpotifyData = await api.spotify.getSpotifyArtist({ id: value });
        setArtistList(spotifyArtistsToOptions(data));
      } catch (err) {
        setError(err);
      }
    },
    [setError]
  );
  const debouncedSearchArtist = useMemo(() => debounce(searchArtist, 300), [searchArtist]);

  const onSubmit = (values: FieldValues) => {
    setError(null);
    const base = {
      artistStatus: values.artistStatus,
      companyKind: values.companyKind,
      artistName: values.artistName,
      email: values.email.trim(),
      language: values.language,
    };
    const identity =
      values.companyKind === 'artist'
        ? {
            spotifyId: values.identityMode === 'spotify' ? values.spotifyId : undefined,
            youtubeId: values.identityMode === 'youtube' ? values.youtubeId : undefined,
          }
        : {};
    const data = values.dealId
      ? { ...base, dealId: values.dealId }
      : {
          ...base,
          ...identity,
          ...(values.artistStatus === 'signed'
            ? {
                hubspotDealId: values.hubspotDealId,
                advanceAmount: values.advanceAmount,
                termLength: values.termLength,
                distributionStatus: values.distributionStatus,
              }
            : {}),
          hasAnalyticsAccess: !!values.hasAnalyticsAccess,
          hasEducationAccess: !!values.hasEducationAccess,
        };
    setSending(true);
    api.invitation
      .createInvitation({ data })
      .then((res) => setMagicLink(res.magicLink))
      .catch(setError)
      .finally(() => setSending(false));
  };

  const startOver = () => {
    setMagicLink('');
    setError(null);
    clearRow();
    reset(defaults);
  };

  const banner =
    row && lookup === 'bad' ? (
      <Alert severity="warning">
        <strong>Already invited.</strong> Deal {row.dealId} already carries this id. Enter a new
        email address to send another invitation.
      </Alert>
    ) : row && lookup === 'noid' ? (
      <Alert severity="error">
        <strong>No HubSpot deal id.</strong> No id, no deal. Add the Record ID in Airtable first.
      </Alert>
    ) : row && lookup === 'blocked' ? (
      <Alert severity="error">
        <strong>Not eligible.</strong>{' '}
        {row.blockers
          .filter((b) => b in ROW_BLOCKERS)
          .map((b) => ROW_BLOCKERS[b])
          .join(', ')}
        . Fix the row in Airtable first.
      </Alert>
    ) : lookup === 'ok' && row ? (
      <Alert severity="success">
        <strong>Found.</strong> {row.name} · {row.kind === 'label' ? 'Label' : 'Artist'}. Fields
        below are prefilled from the customer file.
      </Alert>
    ) : lookup === 'warn' ? (
      <Alert severity="warning">
        <strong>Not found.</strong> No matching row, or the row is incomplete. Check the id or name
        in Airtable.
      </Alert>
    ) : null;

  // Plain JSX, not an inner component: a component defined per render remounts the inputs and drops focus on every keystroke.
  const formBody = (
    <FormProvider {...form}>
      <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column" gap={1}>
          <BaseInputController
            withError
            size="small"
            select
            variant="filled"
            name="artistStatus"
            label="Artist status"
            defaultValue={defaults.artistStatus}
            onChange={(e) => clearRow(e.target.value)}
          >
            <MenuItem value="signed">Signed</MenuItem>
            <MenuItem value="unsigned">Unsigned</MenuItem>
          </BaseInputController>

          {signed && (
            <Autocomplete<CustomerFileHit, false, false, true>
              freeSolo
              options={hits}
              value={row}
              loading={searching}
              filterOptions={(x) => x}
              getOptionLabel={(option) => (typeof option === 'string' ? option : hitLabel(option))}
              isOptionEqualToValue={(a, b) => a.airtableRecordId === b.airtableRecordId}
              noOptionsText="No customer-file row matches"
              onInputChange={(_e, value, reason) => {
                if (reason === 'input') {
                  if (row) clearRow();
                  debouncedSearch(value);
                }
              }}
              onChange={(_e, value) => {
                if (value && typeof value !== 'string') applyRow(value);
                else if (!value) clearRow();
              }}
              renderInput={(params) => (
                <BaseInput
                  {...params}
                  variant="filled"
                  error={false}
                  label="Deal"
                  required
                  placeholder="Deal id, artist name, Spotify or YouTube id"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searching ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}

          {signed && banner}

          {open && (
            <BaseInputController
              withError
              size="small"
              select
              variant="filled"
              name="companyKind"
              label="Customer kind"
              defaultValue={defaults.companyKind}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="artist">Artist</MenuItem>
              <MenuItem value="label">Label</MenuItem>
            </BaseInputController>
          )}

          {artist && (
            <Field label="Artist identity *">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={identityMode}
                onChange={(_e, mode) => {
                  if (!mode) return;
                  set('identityMode', mode);
                  set('spotifyId', null);
                  set('youtubeId', null);
                }}
                sx={{ alignSelf: 'flex-start', marginBottom: 1 }}
              >
                <ToggleButton value="spotify">Spotify artist</ToggleButton>
                <ToggleButton value="youtube">YouTube channel</ToggleButton>
              </ToggleButtonGroup>
              {identityMode === 'spotify' ? (
                <AutocompleteInputController
                  options={artistList}
                  value={spotifyOption}
                  name="spotifyId"
                  withError
                  handleChange={debouncedSearchArtist}
                  additionalOnChange={(value) => {
                    const picked = artistList.find((a) => a.value === value);
                    if (picked && !form.getValues('artistName')) set('artistName', picked.label);
                  }}
                  label="Search artist or paste a Spotify artist id"
                />
              ) : (
                <BaseInputController
                  withError
                  name="youtubeId"
                  type="text"
                  variant="filled"
                  label="YouTube URL or ID"
                />
              )}
            </Field>
          )}

          {open && (
            <BaseInputController
              withError
              name="artistName"
              type="text"
              variant="filled"
              label="Name"
            />
          )}

          {open && (
            <BaseInputController
              withError
              name="email"
              type="email"
              variant="filled"
              label="Email"
              fullWidth
            />
          )}

          {found && (
            <>
              <BaseInputController
                withError
                name="advanceAmount"
                type="number"
                inputMode="numeric"
                variant="filled"
                label="Advance amount"
              />
              <BaseInputController
                withError
                name="termLength"
                type="number"
                inputMode="numeric"
                variant="filled"
                label="Term length (years)"
              />
              <BaseInputController
                withError
                size="small"
                select
                variant="filled"
                name="distributionStatus"
                label="Distribution status"
                defaultValue="other"
                SelectProps={{ displayEmpty: false }}
              >
                {Object.entries(DISTRIBUTION_LABELS).map(([value, label]) => (
                  <MenuItem value={value} key={value}>
                    {label}
                  </MenuItem>
                ))}
              </BaseInputController>
            </>
          )}

          {open && (
            <Box>
              <Box display="flex" alignItems="center">
                <CheckboxController name="hasAnalyticsAccess" disabled={alreadyInvited} />
                <Text fontSize={17}>Access to Analytics</Text>
              </Box>
              <Box display="flex" alignItems="center" marginBottom={1}>
                <CheckboxController name="hasEducationAccess" disabled={alreadyInvited} />
                <Text fontSize={17}>Access to Education</Text>
              </Box>
              {alreadyInvited && (
                <Text fontSize={13} color="#666" marginBottom={1}>
                  Copied from the deal&apos;s first invitation.
                </Text>
              )}
            </Box>
          )}

          {open && (
            <BaseInputController
              withError
              size="small"
              select
              variant="filled"
              name="language"
              label="Invitation language"
              defaultValue={defaults.language}
              SelectProps={{ displayEmpty: false }}
            >
              {Object.entries(supportedLanguages).map(([languageKey, languageLabel]) => (
                <MenuItem value={languageKey} key={languageKey}>
                  {languageLabel}
                </MenuItem>
              ))}
            </BaseInputController>
          )}

          {message && (
            <Text color={colors.system.error} lineHeight={1.3} marginBottom={1}>
              {message}
            </Text>
          )}

          <Box>
            <StyledButton type="submit" disabled={(signed && !found) || sending} loading={sending}>
              Send invitation
            </StyledButton>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );

  const magicLinkBody = (
    <Box>
      <Box paddingTop={1}>
        <BaseInput
          sx={{ width: '100%' }}
          label="Magic Link"
          value={magicLink}
          variant="filled"
          error={false}
          helperText="Copy and send this link to the artist. It will allow them to login into the SongFund platform. An email will also be sent to the artist with the link."
        />
      </Box>
      <Box marginTop={1} display="flex" gap={2}>
        <StyledButton onClick={() => copyToClipboard(magicLink)}>Copy Magic Link</StyledButton>
        <StyledButton variant="outlined" onClick={startOver}>
          New invitation
        </StyledButton>
      </Box>
    </Box>
  );

  return (
    <Box padding={2} maxWidth={600} margin="auto">
      <Box borderBottom={1} borderColor="#eee" paddingBottom={2} marginBottom={2}>
        {magicLink ? (
          <>
            <Text fontSize={26}>Invitation sent</Text>
            <Text marginTop={1} lineHeight={1.2}>
              The invitation has been sent to the artist with an email containing the magic link.
            </Text>
          </>
        ) : (
          <>
            <Text fontSize={26}>Invite an artist</Text>
            <Text marginTop={1} lineHeight={1.2}>
              The invitation will create a new account for the artist, allowing access to features
              like Analytics and Education through a magic link.
            </Text>
          </>
        )}
      </Box>

      <Box>{magicLink ? magicLinkBody : formBody}</Box>
    </Box>
  );
}
